import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export function DeliveryChargesPopup({
    open,
    onOpenChange,
    currentDeliveryCharges = 0,
    onApplyDeliveryCharges
}) {
    const MAX_CUSTOM_CHARGE_DIGITS = 5;
    const [selectedAmount, setSelectedAmount] = useState(null);
    const [customAmount, setCustomAmount] = useState("");

    // Delivery charge options: 50, 100, 150, 200, 250, 300, 350, 400, 450, 500
    const deliveryChargeOptions = [50, 100, 150, 200, 250, 300, 350, 400, 450, 500];

    // Initialize selected amount when popup opens
    useEffect(() => {
        if (open) {
            // If current delivery charges match one of the options, select it
            const matchingOption = deliveryChargeOptions.find(option => option === currentDeliveryCharges);
            setSelectedAmount(matchingOption || null);
            if (!matchingOption && currentDeliveryCharges > 0) {
                setCustomAmount(String(currentDeliveryCharges));
            } else {
                setCustomAmount("");
            }
        }
    }, [open, currentDeliveryCharges]);

    const handleApply = () => {
        const parsedCustomAmount = parseFloat(customAmount);
        const hasValidCustomAmount = customAmount !== "" && !Number.isNaN(parsedCustomAmount) && parsedCustomAmount >= 0;
        const amountToApply = hasValidCustomAmount ? parsedCustomAmount : selectedAmount;

        if (amountToApply !== null && onApplyDeliveryCharges) {
            onApplyDeliveryCharges(amountToApply);
        }
        onOpenChange(false);
    };

    const handleRemove = () => {
        if (onApplyDeliveryCharges) {
            onApplyDeliveryCharges(0);
        }
        setSelectedAmount(null);
        setCustomAmount("");
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Select Delivery Charges</DialogTitle>
                    <DialogDescription>
                        Choose the delivery charges amount for this order.
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto pr-2">
                        {deliveryChargeOptions.map((amount) => {
                            const isSelected = selectedAmount === amount;

                            return (
                                <Button
                                    key={amount}
                                    variant={isSelected ? "default" : "outline"}
                                    onClick={() => {
                                        setSelectedAmount(amount);
                                        setCustomAmount("");
                                    }}
                                    className={`h-auto py-4 px-4 flex items-center justify-center text-lg font-semibold ${isSelected
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "hover:bg-accent"
                                        }`}
                                >
                                    Rs {amount}
                                </Button>
                            );
                        })}
                    </div>

                    <div className="mt-4 space-y-2">
                        <label className="text-sm font-medium">Or enter custom delivery charges</label>
                        <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={customAmount}
                            onChange={(e) => {
                                const digitsOnly = e.target.value.replace(/\D/g, "");
                                const limitedDigits = digitsOnly.slice(0, MAX_CUSTOM_CHARGE_DIGITS);
                                setCustomAmount(limitedDigits);
                                setSelectedAmount(null);
                            }}
                            placeholder="Enter delivery charges"
                        />
                    </div>
                </div>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    {currentDeliveryCharges > 0 && (
                        <Button
                            variant="destructive"
                            onClick={handleRemove}
                            className="w-full sm:w-auto"
                        >
                            Remove Delivery Charges
                        </Button>
                    )}
                    <div className="flex gap-2 w-full sm:w-auto">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="flex-1 sm:flex-none"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleApply}
                            disabled={selectedAmount === null && customAmount === ""}
                            className="flex-1 sm:flex-none"
                        >
                            Apply
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

